import { Box, Grid, TablePagination, Typography } from "@mui/material";
import { MouseEvent, ReactNode } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface ColumnLabel {
  label: string;
  // Optional per-column flex weight, matching the flex passed to that
  // column's TableCell — lets a header/row pair rebalance column widths
  // (e.g. a narrow Sl. No. column, a wider Name column) instead of every
  // non-last column defaulting to an equal 1.
  flex?: number;
}

interface TableHeaderRowProps {
  labels: ColumnLabel[];
  lastColumnWeight?: number;
  lastColumnWidth?: number;
  align?: "left" | "center";
  // Opt-in grid-line borders around the header + every column, for visually
  // verifying column alignment — off everywhere else by default so this
  // doesn't change how any other table looks.
  bordered?: boolean;
}

// Shared grid-line color for every "bordered" table across the app (also
// reused directly by modules that don't build on TableCell/TableRow, e.g.
// the MUI DataGrid-based Needs list).
export const BORDER_COLOR = "#D8CBD8";

export function TableHeaderRow({
  labels,
  lastColumnWeight = 1,
  lastColumnWidth,
  align = "left",
  bordered = false,
}: TableHeaderRowProps) {
  return (
    <Grid
      container
      direction="row"
      sx={{
        display: { xs: "none", sm: "flex" },
        bgcolor: purpleLight,
        borderRadius: bordered ? 0 : 2,
        border: bordered ? `1px solid ${BORDER_COLOR}` : undefined,
        borderTopLeftRadius: bordered ? 8 : undefined,
        borderTopRightRadius: bordered ? 8 : undefined,
        py: bordered ? 0 : 1.25,
        px: 2,
      }}
    >
      {labels.map((item, index) => (
        <Grid
          item
          flex={
            index === labels.length - 1
              ? lastColumnWidth
                ? `0 0 ${lastColumnWidth}px`
                : lastColumnWeight
              : item.flex ?? 1
          }
          minWidth={0}
          key={item.label}
          sx={{
            borderRight:
              bordered && index !== labels.length - 1 ? `1px solid ${BORDER_COLOR}` : undefined,
            px: bordered ? 1.5 : undefined,
            py: bordered ? 1.25 : undefined,
            display: bordered ? "flex" : undefined,
            alignItems: bordered ? "center" : undefined,
            "&:first-of-type": bordered ? { pl: 0 } : undefined,
          }}
        >
          <Typography
            fontSize={12}
            fontWeight={700}
            color={purple}
            textAlign={align}
            noWrap
            sx={{ textTransform: "uppercase", letterSpacing: 0.4, textOverflow: "ellipsis" }}
          >
            {item.label}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

export function TableEmptyState({
  message = "No data available",
  hint = "Try adjusting your search or filters.",
}: {
  message?: string;
  hint?: string;
}) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flex={1}
      py={8}
      gap={0.5}
    >
      <Typography fontWeight={600} color={black}>
        {message}
      </Typography>
      <Typography variant="body2" color={greyEnabled}>
        {hint}
      </Typography>
    </Box>
  );
}

interface TableFooterPaginationProps {
  count: number;
  page: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  summaryText: string;
  rowsPerPage?: number;
  // Both omitted (the default everywhere else): the rows-per-page selector
  // stays hidden, same as before. Pass both to let the caller offer a
  // records-per-page picker.
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
}

export function TableFooterPagination({
  count,
  page,
  onPageChange,
  summaryText,
  rowsPerPage = 8,
  rowsPerPageOptions = [],
  onRowsPerPageChange,
}: TableFooterPaginationProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        gap: 1,
      }}
    >
      <Typography variant="body2" color={greyEnabled}>
        {summaryText}
      </Typography>
      <TablePagination
        component="div"
        labelDisplayedRows={() => ""}
        labelRowsPerPage={rowsPerPageOptions.length ? "Rows per page:" : ""}
        rowsPerPageOptions={rowsPerPageOptions}
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={
          onRowsPerPageChange
            ? (e) => onRowsPerPageChange(parseInt(e.target.value, 10))
            : undefined
        }
      />
    </Box>
  );
}

export function TableRow({
  children,
  onClick,
  bordered = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  bordered?: boolean;
}) {
  return (
    <Box
      onClick={onClick}
      display="flex"
      flexDirection="column"
      width="100%"
      sx={{
        cursor: onClick ? "pointer" : "default",
        borderBottom: `1px solid ${bordered ? BORDER_COLOR : "#F1E9F1"}`,
        borderLeft: bordered ? { xs: "none", sm: `1px solid ${BORDER_COLOR}` } : undefined,
        borderRight: bordered ? { xs: "none", sm: `1px solid ${BORDER_COLOR}` } : undefined,
        transition: "background-color 0.15s ease",
        ":hover": onClick ? { backgroundColor: purpleLight } : undefined,
        "&:last-of-type": bordered
          ? { borderBottomLeftRadius: { xs: 0, sm: 8 }, borderBottomRightRadius: { xs: 0, sm: 8 } }
          : { borderBottom: "none" },
      }}
    >
      <Grid
        display="flex"
        flex={1}
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: bordered ? "stretch" : "center" }}
        gap={{ xs: 1, sm: 0 }}
        py={bordered ? { xs: 2, sm: 0 } : 2}
        px={2}
      >
        {children}
      </Grid>
    </Box>
  );
}

// Pairs a value with its column label so a table row can render as a
// labelled card below `sm` (mobile) — the header row is hidden there
// (see TableHeaderRow), so each cell shows its own caption instead. At
// `sm` and up, this renders exactly like the old bare `Grid item` cell
// callers used to write inline (label hidden, same flex-basis rules).
export function TableCell({
  label,
  children,
  flex = 1,
  lastColumnWidth,
  isLast = false,
  bordered = false,
}: {
  label?: string;
  children: ReactNode;
  flex?: number;
  lastColumnWidth?: number;
  isLast?: boolean;
  bordered?: boolean;
}) {
  return (
    <Grid
      item
      flex={{
        xs: "1 1 auto",
        sm: isLast && lastColumnWidth ? `0 0 ${lastColumnWidth}px` : flex,
      }}
      minWidth={0}
      sx={{
        borderRight: bordered && !isLast ? { xs: "none", sm: `1px solid ${BORDER_COLOR}` } : undefined,
        px: bordered ? { xs: 0, sm: 1.5 } : undefined,
        py: bordered ? { xs: 0, sm: 2 } : undefined,
        "&:first-of-type": bordered ? { pl: { xs: 0, sm: 0 } } : undefined,
        ...(bordered && {
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", sm: "center" },
        }),
      }}
    >
      {label && (
        <Typography
          sx={{
            display: { xs: "block", sm: "none" },
            fontSize: 11,
            fontWeight: 700,
            color: purple,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
      )}
      {children}
    </Grid>
  );
}
